export type StrapiFilterOperator =
    | '$eq'
    | '$ne'
    | '$lt'
    | '$lte'
    | '$gt'
    | '$gte'
    | '$contains'
    | '$containsi'
    | '$startsWith'
    | '$endsWith'
    | '$in'
    | '$notIn'
    | '$null'
    | '$notNull'
    | '$between';

export type StrapiFilterValue<T = string> = T | T[];

export type StrapiFilterOperatorValue<T = string> = {
    [K in StrapiFilterOperator]?: StrapiFilterValue<T>;
};

export type StrapiFilter<T = string> =
    | StrapiFilterValue<T>
    | StrapiFilterOperatorValue<T>
    | { $or?: StrapiFilter<T>[] }
    | { $and?: StrapiFilter<T>[] };

export type StrapiSortDirection = 'asc' | 'desc';

export type StrapiSort = string | string[];

export interface StrapiPagination {
    page?: number;
    pageSize?: number;
    withCount?: boolean;
    start?: number;
    limit?: number;
}

export type StrapiPopulate = string | string[] | '*';

export type StrapiFields = string | string[];

export type StrapiPublicationState = 'live' | 'preview';

export interface StrapiLocaleFilter {
    locale?: StrapiFilter<string>;
}

export interface StrapiBaseQueryOptions {
    sort?: StrapiSort;
    pagination?: StrapiPagination;
    populate?: StrapiPopulate;
    fields?: StrapiFields;
    publicationState?: StrapiPublicationState;
    locale?: string;
}

export type BlogArticleSortField =
    | 'publishedAt'
    | 'createdAt'
    | 'updatedAt'
    | 'title'
    | 'slug'
    | 'author.name'
    | 'author.id'
    | 'category.name'
    | 'category.id';

export type BlogArticleSort =
    | `${BlogArticleSortField}:${StrapiSortDirection}`
    | Array<`${BlogArticleSortField}:${StrapiSortDirection}`>;

export interface StrapiPaginationMeta {
    page: number;
    pageSize: number;
    pageCount: number;
    total: number;
}

export interface StrapiListResponse<T> {
    data: T[];
    meta: {
        pagination: StrapiPaginationMeta;
    };
}

interface Props<T extends StrapiBaseQueryOptions = StrapiBaseQueryOptions> {
    endpoint: string;
    query?: Record<string, string> | T;
    wrappedByKey?: string;
    wrappedByList?: boolean;
}

interface FetchApiClientOptions {
    baseUrl: string;
    token?: string;
}

type StrapiErrorResponse = {
    error?: {
        message?: string;
    };
};

function isStrapiQueryOptions(
    query: Record<string, string> | StrapiBaseQueryOptions
): query is StrapiBaseQueryOptions {
    return (
        typeof query === 'object' &&
        query !== null &&
        ('filters' in query ||
            'sort' in query ||
            'pagination' in query ||
            'populate' in query ||
            'fields' in query ||
            'publicationState' in query ||
            'locale' in query)
    );
}

function processFilterValue(
    value: StrapiFilter,
    prefix: string,
    query: Record<string, string>
): void {
    if (value === null || value === undefined) {
        return;
    }

    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        query[prefix] = String(value);
        return;
    }

    if (Array.isArray(value)) {
        value.forEach((item, index) => {
            if (typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean') {
                query[`${prefix}[${index}]`] = String(item);
            }
        });
        return;
    }

    if (typeof value === 'object') {
        if ('$or' in value && Array.isArray(value.$or)) {
            value.$or.forEach((orFilter, orIndex) => {
                if (typeof orFilter === 'object' && orFilter !== null) {
                    Object.entries(orFilter).forEach(([key, val]) => {
                        processFilterValue(
                            val as StrapiFilter,
                            `${prefix}[$or][${orIndex}][${key}]`,
                            query
                        );
                    });
                }
            });
        } else if ('$and' in value && Array.isArray(value.$and)) {
            value.$and.forEach((andFilter, andIndex) => {
                if (typeof andFilter === 'object' && andFilter !== null) {
                    Object.entries(andFilter).forEach(([key, val]) => {
                        processFilterValue(
                            val as StrapiFilter,
                            `${prefix}[$and][${andIndex}][${key}]`,
                            query
                        );
                    });
                }
            });
        } else {
            Object.entries(value).forEach(([operator, operatorValue]) => {
                if (operator.startsWith('$')) {
                    if (Array.isArray(operatorValue)) {
                        operatorValue.forEach((item, index) => {
                            query[`${prefix}[${operator}][${index}]`] = String(item);
                        });
                    } else if (
                        typeof operatorValue === 'object' &&
                        operatorValue !== null &&
                        !Array.isArray(operatorValue)
                    ) {
                        Object.entries(operatorValue).forEach(([deepKey, deepValue]) => {
                            query[`${prefix}[${operator}][${deepKey}]`] = String(deepValue);
                        });
                    } else {
                        query[`${prefix}[${operator}]`] = String(operatorValue);
                    }
                }
            });
        }
    }
}

export function buildStrapiQuery(options: StrapiBaseQueryOptions): Record<string, string> {
    const query: Record<string, string> = {};

    if (options.sort) {
        const sorts = Array.isArray(options.sort) ? options.sort : [options.sort];
        sorts.forEach((s: string, i: number) => {
            query[`sort[${i}]`] = s;
        });
    }

    if (options.pagination) {
        Object.entries(options.pagination).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                query[`pagination[${key}]`] = String(value);
            }
        });
    }

    if (options.populate) {
        if (options.populate === '*') {
            query['populate'] = '*';
        } else {
            const populates = Array.isArray(options.populate)
                ? options.populate
                : [options.populate];
            populates.forEach((p: string, i: number) => {
                query[`populate[${i}]`] = p;
            });
        }
    }

    if (options.fields) {
        const fields = Array.isArray(options.fields) ? options.fields : [options.fields];
        fields.forEach((f: string, i: number) => {
            query[`fields[${i}]`] = f;
        });
    }

    if (options.publicationState) {
        query['publicationState'] = options.publicationState;
    }

    if (options.locale) {
        query['locale'] = options.locale;
    }

    if ('filters' in options && options.filters) {
        Object.entries(options.filters).forEach(([field, filter]) => {
            processFilterValue(filter as StrapiFilter, `filters[${field}]`, query);
        });
    }

    return query;
}

function normalizeBaseUrl(baseUrl: string | undefined, clientName: string): string {
    if (!baseUrl) {
        throw new Error(
            `[Strapi API] Missing ${clientName} baseUrl. Check STRAPI_URL in your Vite env configuration.`
        );
    }

    return baseUrl;
}

export function createFetchListApi({ baseUrl, token }: FetchApiClientOptions) {
    return async function fetchListApiClient<
        T,
        Q extends StrapiBaseQueryOptions = StrapiBaseQueryOptions,
    >({
        endpoint,
        query,
    }: Omit<Props<Q>, 'wrappedByKey' | 'wrappedByList'>): Promise<StrapiListResponse<T>> {
        const resolvedBaseUrl = normalizeBaseUrl(baseUrl, 'fetchListApiClient');

        try {
            if (endpoint.startsWith('/')) {
                endpoint = endpoint.slice(1);
            }

            const url = new URL(`${resolvedBaseUrl}/api/${endpoint}`);

            if (query) {
                const queryObj = isStrapiQueryOptions(query) ? buildStrapiQuery(query) : query;

                Object.entries(queryObj).forEach(([key, value]) => {
                    url.searchParams.append(key, value);
                });
            }

            const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};

            const res = await fetch(url.toString(), { headers });
            const payload = (await res.json()) as unknown;

            if (!res.ok) {
                const errorPayload = payload as StrapiErrorResponse;
                const detail = errorPayload?.error?.message
                    ? ` - ${errorPayload.error.message}`
                    : '';
                console.warn(
                    `[Strapi API] List request failed (${res.status} ${res.statusText})${detail} for ${endpoint}`
                );
                return {
                    data: [],
                    meta: { pagination: { page: 1, pageSize: 0, pageCount: 0, total: 0 } },
                };
            }

            return payload as StrapiListResponse<T>;
        } catch (error) {
            console.warn(
                `[Strapi API] Network error for ${endpoint}:`,
                error instanceof Error ? error.message : 'Unknown error'
            );
            return {
                data: [],
                meta: { pagination: { page: 1, pageSize: 0, pageCount: 0, total: 0 } },
            };
        }
    };
}
